"use client"

import * as React from "react"
import { ChevronsUpDown } from "lucide-react"
import { TransactionPurpose } from '@prisma/client';
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { PurposeSelectorProps } from "./purpose-selector.types";
import { Purpose, GroupedPurpose } from "@/components/ui/purpose";
import { TransactionContext, TransactionContextType } from "@/app/report/transactions/context";
import { useContext, useEffect } from "react";


const PurposeSelector: React.FC<PurposeSelectorProps> = ({ onChange }) => {
  const [open, setOpen] = React.useState(false)

  const { selectedTransaction } = useContext(TransactionContext) as TransactionContextType; 
  const [selectedPurpose, setSelectedPurpose] = React.useState<TransactionPurpose | null>(selectedTransaction?.purpose || null)

  useEffect(() => {
    setSelectedPurpose(selectedTransaction?.purpose || null)
  }, [selectedTransaction, selectedTransaction?.purpose])

  const handlePurposeChange = (purpose: TransactionPurpose) => {
    setSelectedPurpose(purpose)
    onChange(purpose)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {selectedPurpose
            ? <Purpose purpose={selectedPurpose} />
            : "Select a purpose..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command>
          <CommandInput variant="ghost" className="py-3" placeholder="Search purpose..." />
          <CommandList>
            <CommandEmpty>No purpose found.</CommandEmpty>
            {Object.entries(GroupedPurpose).map(([type, purposes]) => (
              <CommandGroup key={type} heading={type.charAt(0) + type.slice(1).toLowerCase()}>
                {purposes.map((purpose) => (
                  <CommandItem
                    key={purpose}
                    value={purpose}
                    selected={selectedPurpose === purpose}
                    onSelect={(currentValue) => {
                      handlePurposeChange(currentValue as TransactionPurpose)
                      setOpen(false)
                    }}
                  >
                    <Purpose purpose={purpose as TransactionPurpose} />
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export { PurposeSelector }
